import {
  Box,
  Button,
  Typography,
  TextField,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { useState, useEffect } from "react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { SquarePen } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useHotel } from "../context/HotelContext";
import LoadingGif from "../components/LoadingGif";

const STEPS = [
  { key: "hotelInfo", labelKey: "StepHotelInfo" },
  { key: "licenseVerification", labelKey: "StepLicenseVerification" },
  { key: "roomInfo", labelKey: "StepRoomInfo" },
  { key: "additionalInfo", labelKey: "StepAdditionalInfo" },
  { key: "paymentMethod", labelKey: "StepPaymentMethod" },
];

const CURRENT_STEP_INDEX = 4;

const HotelAdd_WPayment_1 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isMobile = useMediaQuery("(max-width:950px)");
  const { saveStep, fetchDraft, hotelData, loading, } = useHotel();
  const [isSaving, setIsSaving] = useState(false);
  const edit = searchParams.get('edit') || '';
  const [allowEdit, setAllowEdit] = useState(false);

  const [formData, setFormData] = useState({
    accountName: "",
    bankName: "",
    branchCode: "",
    accountNumber: "",
    lastAccDigit: 0,
  });

  useEffect(() => {
    if (id) {
      fetchDraft(id);
    }
  }, [id, fetchDraft]);

  useEffect(() => {
    if (hotelData.payment) {
      setFormData({
        accountName: hotelData.payment.accountName || "",
        bankName: hotelData.payment.bankName || "",
        branchCode: hotelData.payment.branchCode || "",
        lastAccDigit: hotelData.payment.lastAccDigit || 0,
      });
    }
  }, [hotelData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = async () => {
    try {
      setIsSaving(true);

      if (allowEdit === false) {
        navigate("/hotel/add/payment-2/" + id);
        return;
      }

      const payment = {
        accountName: formData.accountName,
        bankName: formData.bankName,
        branchCode: formData.branchCode,
        accountNumber: formData.accountNumber,
      };

      // Save additional services step
      await saveStep("payment", payment);

      // Navigate to next step
      if (edit) {
        navigate(`/hotel/add/payment-review/${id}`);
      } else {
        navigate("/hotel/add/payment-2/" + id);
      }
    } catch (err) {
      console.error("Error saving additional services:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndLeave = async () => {
    try {
      setIsSaving(true);

      if (allowEdit === false) {
        // No changes made, just navigate away
        navigate("/hotel/onboarding");
        return;
      }

      const payment = {
        accountName: formData.accountName,
        bankName: formData.bankName,
        branchCode: formData.branchCode,
        accountNumber: formData.accountNumber,
      };

      // Save additional services step
      await saveStep("payment", payment);

      // Navigate to next step
      navigate("/hotel/onboarding");
    } catch (err) {
      console.error("Error saving additional services:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditClick = () => {
    setAllowEdit(true);
    setFormData({
      accountName: "",
      bankName: "",
      branchCode: "",
      accountNumber: "",
      lastAccDigit: 0,
    });
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      borderRadius: "10px",
    },
  };

  const labelSx = { fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 0.5 };
  const descSx = { fontSize: isMobile ? "12px" : "13px", color: "#999", mb: 1.5 };

  const isNextDisabled =
    !formData.accountName ||
    !formData.bankName ||
    !formData.branchCode ||
    !(formData.lastAccDigit || formData.accountNumber);

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
    maxWidth: "1600px",
    width: "100%",
  };

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>
        {/* Stepper */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 6, mx: "auto" }}>
          {STEPS.map((step, index) => (
            <Box key={step.key} sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: isMobile ? "20px" : "30px",
                    height: isMobile ? "20px" : "30px",
                    borderRadius: "50%",
                    backgroundColor: index <= CURRENT_STEP_INDEX ? "#31BACD" : "transparent",
                    border: index <= CURRENT_STEP_INDEX ? "3px solid #fff" : "2px solid #D1D1D1",
                    boxShadow: index <= CURRENT_STEP_INDEX ? "0 0 0 2px #31BACD" : "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: isMobile ? "10px" : "13px",
                    fontWeight: index === CURRENT_STEP_INDEX ? "600" : "400",
                    color: index === CURRENT_STEP_INDEX ? "#000" : "#666",
                    textAlign: "center",
                    whiteSpace: "wrap",
                    width: isMobile ? "50px" : "100px",
                  }}
                >
                  {t(step.labelKey)}
                </Typography>
              </Box>
              {index < STEPS.length - 1 && (
                <Box
                  sx={{
                    height: "1px",
                    width: isMobile ? "30px" : "200px",
                    backgroundColor: "#D1D1D1",
                    mx: "auto",
                    mb: isMobile ? "18px" : "24px",
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        <Box sx={{ width: isMobile ? "100%" : "60%", mx: "auto", display: "flex", flexDirection: "column" }}>
          {/* Heading */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", flexDirection: "column" }}>
              <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#000", mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                {t('PaymentStep1Title')}
                <Box component="span" sx={{ fontWeight: "400", fontSize: isMobile ? "12px" : "14px", color: "#666" }}>
                  {t('MustFill')} <span style={{ color: "red" }}>*</span><span> )</span>
                </Box>
              </Typography>
              <Typography sx={{ fontSize: isMobile ? "13px" : "14px", color: "#666", mb: 4 }}>
                {t('PaymentStep1Desc')}
              </Typography>
            </Box>
            {hotelData.payment && (
              <Button sx={{
                width: "100px", height: "auto", backgroundColor: "#FFB60D", color: "#fff",
                textTransform: "none", display: "flex", alignItems: "center", justifyContent: "center", gap: 1, borderRadius: "5px",
                "&:hover": { backgroundColor: "#e4bf67" },
              }}
                onClick={handleEditClick}>
                <SquarePen size={16} /> {t('Update')}
              </Button>
            )}
          </Box>

          {loading ? (
            <LoadingGif />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
              <Box>
                <Typography sx={labelSx}>
                  {t('AccountNameLabel')}<span style={{ color: "red" }}> *</span>
                </Typography>
                <Typography sx={descSx}>{t('AccountNameDesc')}</Typography>
                {allowEdit || !hotelData.payment ? (
                  <TextField
                    fullWidth
                    value={formData.accountName}
                    onChange={(e) => handleInputChange("accountName", e.target.value)}
                    sx={textFieldSx}
                  />
                ) : (
                  <TextField
                    fullWidth
                    disabled={true}
                    value={formData.accountName}
                    sx={textFieldSx}
                  />
                )}
              </Box>

              <Box>
                <Typography sx={labelSx}>
                  {t('BankNameLabel')}<span style={{ color: "red" }}> *</span>
                </Typography>
                {allowEdit || !hotelData.payment ? (
                  <TextField
                    fullWidth
                    value={formData.bankName}
                    onChange={(e) => handleInputChange("bankName", e.target.value)}
                    sx={textFieldSx}
                  />
                ) : (
                  <TextField
                    fullWidth
                    disabled={true}
                    value={formData.bankName}
                    sx={textFieldSx}
                  />
                )}
              </Box>

              <Box>
                <Typography sx={labelSx}>
                  {t('BranchCodeLabel')}<span style={{ color: "red" }}> *</span>
                </Typography>
                <Typography sx={descSx}>{t('BranchCodeDesc')}</Typography>
                {allowEdit || !hotelData.payment ? (
                  <TextField
                    fullWidth
                    value={formData.branchCode}
                    onChange={(e) => handleInputChange("branchCode", e.target.value)}
                    sx={textFieldSx}
                  />
                ) : (
                  <TextField
                    fullWidth
                    disabled={true}
                    value={formData.branchCode}
                    sx={textFieldSx}
                  />
                )}
              </Box>

              <Box>
                <Typography sx={labelSx}>
                  {t('AccountNumberLabel')}<span style={{ color: "red" }}> *</span>
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                  {allowEdit || !hotelData.payment ? (
                    <TextField
                      fullWidth
                      type="number"
                      value={formData.accountNumber}
                      onChange={(e) => handleInputChange("accountNumber", e.target.value)}
                      sx={textFieldSx}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      type="string"
                      disabled={true}
                      value={`*******${formData.lastAccDigit}`}
                      sx={textFieldSx}
                    />
                  )}
                </Box>

              </Box>
            </Box>
          )}

          {/* Bottom actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              mt: 6,
            }}
          >
            <Button
              onClick={handleSaveAndLeave}
              disabled={isNextDisabled || isSaving}
              sx={{
                color: "#666",
                textTransform: "none",
                fontSize: isMobile ? "14px" : "16px",
                textDecoration: "underline",
                "&:disabled": { opacity: 0.5 },
              }}
            >
              {t('SaveAndLeave')}
            </Button>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate("/hotel/add/room-info-add-2/" + id)}
                sx={{
                  color: "#666",
                  borderColor: "#D1D1D1",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { borderColor: "#999", backgroundColor: "transparent" },
                }}
              >
                {t('PreviousStep')}
              </Button>
              <Button
                variant="contained"
                disabled={isNextDisabled || isSaving}
                onClick={handleNext}
                sx={{
                  backgroundColor: "#31BACD",
                  color: "#fff",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                  "&:disabled": { opacity: 0.5, color: "#fff" },
                }}
              >
                {isSaving ? <CircularProgress size={20} color="inherit" /> : t('NextStep')}
              </Button>
            </Box>
          </Box>
        </Box>
      </div>
      <Footer />
    </div>
  );
};

export default HotelAdd_WPayment_1;
